import { z } from 'zod';
import { allHtmlParsers } from '@utils/ast';
import { bindInputMaker } from '../util.make-input.js';

const makeInput = bindInputMaker();
const textInput = makeInput({
  entry: {
    schema: z.object({
      type: z.literal("text").default("text"),
      value: z.string().default("")
    }),
    shouldValidate: node => !!node.validation.correctAnswers.length,
    validate: (node, entry, context) => {
      const isCorrect = node.validation.correctAnswers.some(answer => cleanText(answer, node.validation) === cleanText(entry.value, node.validation));
      if (!isCorrect) {
        return false;
      }

      //
      // NB: In case the current node is part of an answerGroup, then we must
      // ensure that repeats of the same answer is used across entries.
      //
      if (node.validation.answerGroupId) {
        const contestants = context.inputs.filter(it => {
          const entry = textInput.entry.asTypeSafe(it.entry);
          if (!entry) {
            return false;
          }
          return it.node.id !== node.id && it.node.nodeType === "text-input" && it.node.validation.answerGroupId === node.validation.answerGroupId;
        });
        const conflictingEntries = contestants.filter(contenstant => cleanText(entry.value, node.validation) === cleanText(contenstant.entry.value, node.validation));
        if (conflictingEntries.length) {
          const lowestConflictingIndex = Math.min(...conflictingEntries.map(it => it.node.index));
          const isFirstInput = lowestConflictingIndex > node.index;
          return isFirstInput;
        }
      }
      return isCorrect;
    },
    parseLegacyDetailItem: (item, context) => {
      if (context.relatedTask.taskType === "WriteAnswers" && context.relatedTask.answerType === "InText" && typeof item === "string") {
        return [context.relatedInput?.id ?? `${context.relatedTask.id}-${context.indexInDetails}`, {
          type: "text",
          value: item
        }];
      }
    }
  },
  parsers: {
    textinput: (node, context) => {
      const inputContext = context.extra;
      const index = Array.from(node.parentElement?.children ?? []).filter(child => !Object.keys(allHtmlParsers).includes(child.tagName.toLowerCase())).indexOf(node);
      const targetAnswerGroupName = node.querySelector("correctanswergroup")?.textContent ?? "";
      const correctAnswers = (() => {
        const targetAnswerGroup = inputContext?.answerGroups?.[targetAnswerGroupName];
        if (targetAnswerGroup) {
          return targetAnswerGroup;
        }
        return Array.from(node.querySelectorAll("correctanswer")).map(it => it.textContent ?? "");
      })();
      const config = {
        ignoreWhitespace: node.getAttribute("ignoreWhitespace") === "true",
        caseSensitive: node.getAttribute("caseSensitive") === "true"
      };
      return {
        id: `${inputContext?.taskId ? `${inputContext.taskId}-` : ""}${index}`,
        index,
        nodeType: "text-input",
        styling: {
          stickToSide: node.getAttribute("stickToSide"),
          extendSizeBy: Number(node.getAttribute("extendSizeBy") || 0)
        },
        validation: {
          ...config,
          answerGroupId: targetAnswerGroupName,
          correctAnswers: correctAnswers.map(it => cleanText(it, config))
        }
      };
    }
  },
  cleanText
});
function cleanText(text, options) {
  // Normalize whitespace
  if (!options.ignoreWhitespace) {
    text = String(text).replace(/^\s+/, "").replace(/\s+$/, "").replace(/\s+/g, " ");
  } else {
    text = String(text).replace(/\s/g, "");
  }

  // Make canse insensitive?
  if (!options.caseSensitive) {
    text = text.toLowerCase();
  }

  // To make sure that validate accepts all variants of apostrophes
  // See https://dev.azure.com/gyldendaldenmark/GU-Produkter/_workitems/edit/93957
  text = text.replace(/[’‛‘´`′‵]/, "'");
  return text;
}

export { textInput };
//# sourceMappingURL=input.text.js.map
